<template lang="pug">
.sweepstakes-panel(v-if="sweepstakes")
  .sweepstakes-panel__status(:class="`sweepstakes-panel__status--${lifecycle.status}`")
    span {{ lifecycle.label }}

  .sweepstakes-panel__meta(v-if="showMeta")
    p.sweepstakes-panel__meta-line(v-if="sweepstakes.numberOfWinners != null")
      | Winners: {{ winnerCount }} / {{ sweepstakes.numberOfWinners }}
    p.sweepstakes-panel__meta-line(v-if="sweepstakes.losersNotifiedAt")
      | Losers notified {{ formatDate(sweepstakes.losersNotifiedAt) }}

  .sweepstakes-panel__actions(v-if="showActions")
    el-button(
      v-if="canDrawEarly"
      type="warning"
      size="small"
      :loading="actionLoading"
      @click="drawEarly(stickerId)"
    ) Draw early

    el-button(
      v-if="canConfirm"
      type="primary"
      size="small"
      :loading="actionLoading"
      @click="confirmSweepstakes(stickerId)"
    ) Close sweepstakes

  .sweepstakes-panel__winners(v-if="displayWinners.length")
    p.sweepstakes-panel__title Winners
    .sweepstakes-panel__winner(
      v-for="winner in displayWinners"
      :key="winner.id"
    )
      .sweepstakes-panel__winner-main
        img.sweepstakes-panel__avatar(
          v-if="winner.avatar"
          :src="winner.avatar"
          :alt="winner.name"
        )
        fai.sweepstakes-panel__avatar-fallback(
          v-else
          type="fal"
          icon="user"
        )
        .sweepstakes-panel__winner-info
          span.sweepstakes-panel__winner-name {{ winner.name }}
          span.sweepstakes-panel__winner-email(v-if="winner.email") {{ winner.email }}
          span.sweepstakes-panel__winner-date(v-if="winner.selectedAt")
            | Selected {{ formatDate(winner.selectedAt) }}
          span.sweepstakes-panel__winner-date(
            v-if="winner.notifiedAt"
          ) Emailed {{ formatDate(winner.notifiedAt) }}
          span.sweepstakes-panel__winner-date.sweepstakes-panel__winner-date--muted(
            v-else-if="winner.selectedAt && sweepstakes.drawnAt"
          ) No email on file
      el-button(
        v-if="canReplace"
        type="default"
        size="small"
        :loading="actionLoading"
        @click="handleReplaceWinner(stickerId, winner.id, winner.name)"
      ) Replace

  .sweepstakes-panel__empty(v-else-if="sweepstakes.drawnAt")
    p No winners were drawn — there may not have been enough eligible entrants.
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue';
import { useSweepstakesActions } from '@/composables/useSweepstakesActions';
import {
  formatSweepstakesDate,
  getSweepstakesLifecycleState,
} from '@/utils/sweepstakesLifecycle';

const PUBLISHED_STATUSES = ['Active', 'Archived'];

export interface SweepstakesWinnerDisplay {
  id: string;
  name: string;
  avatar?: string | null;
  email?: string | null;
  selectedAt?: string | null;
  notifiedAt?: string | null;
}

export interface SweepstakesPanelData {
  status?: string | null;
  isWinnerConfirmed?: boolean | null;
  drawingDateTime?: string | null;
  drawingDateTimeTimezone?: string | null;
  drawnAt?: string | null;
  losersNotifiedAt?: string | null;
  numberOfWinners?: number | null;
  winners?: SweepstakesWinnerDisplay[];
}

export default defineComponent({
  name: 'SweepstakesWinnersPanel',
  props: {
    stickerId: {
      type: String,
      required: true,
    },
    sweepstakes: {
      type: Object as PropType<SweepstakesPanelData | null>,
      default: null,
    },
    cmsWinners: {
      type: Array as PropType<Array<{
        id: string;
        participantName: string;
        participantAvatar?: string;
        participantEmail?: string;
        dateCreated?: string;
        notifiedAt?: string;
      }>>,
      default: () => [],
    },
    formatDrawingDateTime: {
      type: Function as PropType<(date: string, timezone?: string | null) => string>,
      default: undefined,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['refreshed'],
  setup(props, { emit }) {
    const {
      actionLoading, drawEarly, confirmSweepstakes, handleReplaceWinner,
    } = useSweepstakesActions(
      async () => { emit('refreshed'); },
    );

    // The backend draws whatever it is given, Drafts included, so only offer it once published.
    const isPublished = computed(() => (
      !props.sweepstakes?.status || PUBLISHED_STATUSES.includes(props.sweepstakes.status)
    ));

    const lifecycle = computed(() => getSweepstakesLifecycleState(
      {
        drawnAt: props.sweepstakes?.drawnAt,
        isWinnerConfirmed: props.sweepstakes?.isWinnerConfirmed,
        drawingDateTime: props.sweepstakes?.drawingDateTime,
        drawingDateTimeTimezone: props.sweepstakes?.drawingDateTimeTimezone,
        isPublished: isPublished.value,
      },
      props.formatDrawingDateTime,
    ));

    const displayWinners = computed((): SweepstakesWinnerDisplay[] => {
      if (props.sweepstakes?.winners?.length) {
        return props.sweepstakes.winners.map((winner) => ({
          id: winner.id,
          name: winner.name,
          avatar: winner.avatar,
          email: winner.email,
          selectedAt: winner.selectedAt,
          notifiedAt: winner.notifiedAt,
        }));
      }

      return props.cmsWinners.map((winner) => ({
        id: winner.id,
        name: winner.participantName,
        avatar: winner.participantAvatar,
        email: winner.participantEmail,
        selectedAt: winner.dateCreated,
        notifiedAt: winner.notifiedAt,
      }));
    });

    const winnerCount = computed(() => displayWinners.value.length);

    const canDrawEarly = computed(() => (
      props.showActions
      && isPublished.value
      && !props.sweepstakes?.drawnAt
      && !props.sweepstakes?.isWinnerConfirmed
    ));

    const canConfirm = computed(() => (
      props.showActions
      && !!props.sweepstakes?.drawnAt
      && !props.sweepstakes?.isWinnerConfirmed
      && winnerCount.value > 0
    ));

    const canReplace = computed(() => (
      props.showActions
      && !!props.sweepstakes?.drawnAt
      && !props.sweepstakes?.isWinnerConfirmed
    ));

    const showMeta = computed(() => (
      props.sweepstakes?.numberOfWinners != null
      || !!props.sweepstakes?.losersNotifiedAt
    ));

    function formatDate(value: string) {
      return formatSweepstakesDate(value);
    }

    return {
      lifecycle,
      displayWinners,
      winnerCount,
      canDrawEarly,
      canConfirm,
      canReplace,
      showMeta,
      actionLoading,
      drawEarly,
      confirmSweepstakes,
      handleReplaceWinner,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.sweepstakes-panel {
  &__status {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 12px;

    &--scheduled {
      background: #fff8e6;
      color: #8a6d00;
    }

    &--drawn {
      background: #e8f8f6;
      color: #0d6b62;
    }

    &--closed {
      background: #f0f0f0;
      color: #555;
    }
  }

  &__meta {
    margin-bottom: 12px;
  }

  &__meta-line {
    margin: 0 0 4px;
    font-size: 12px;
    color: #666;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 14px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin: 0 0 10px;
  }

  &__winner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 8px;
    margin-bottom: 12px;
    border: 1px solid #efefef;
    border-radius: 4px;
  }

  &__winner-main {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__avatar-fallback {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: #999;
  }

  &__winner-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__winner-name {
    font-weight: 600;
    font-size: 12px;
  }

  &__winner-email,
  &__winner-date {
    font-size: 11px;
    color: #666;
  }

  &__winner-date--muted {
    color: #999;
    font-style: italic;
  }

  &__empty p {
    margin: 0;
    font-size: 12px;
    color: #666;
  }
}
</style>
